/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Component, inject, computed, effect, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import { RouterService } from '../../services/router.services';
import { SIMULATED_ROLE_LABELS } from '../../services/role-simulation.utils';

import { CountBadgeComponent } from '../count-badge/count-badge.component';
import { NavbarService } from './navbar.service';
import { versionEmployee, buildDateEmployee } from 'package.json';

import { UserService } from '../../services/user.service';
import { UpdateCheckService } from '@awo-care/shared-core';
import { ChangesBadgeService } from '../../services/changes-badge.service';
import { LogoWithBadgeComponent } from '@awo-care/shared-ui';
import { environment } from '@awo-care/shared-auth';
import { NavBadgeKey, NavEntry, matchesPath, visibleNavGroups } from '../../navigation/navigation.config';
import { buildProfileTile } from '../../navigation/profile-tile.utils';
import { Employee } from '../models/employee.model';
import { FeedbackButtonComponent, TicketPortalStore } from '@solidgroup-agency/feedback-widget';

@Component({
  selector: 'app-navbar',
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    RouterLink,
    LogoWithBadgeComponent,
    CountBadgeComponent,
    FeedbackButtonComponent,
  ],
  templateUrl: './navbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent {
  userService = inject(UserService);
  navbarService = inject(NavbarService);
  private readonly updateCheckService = inject(UpdateCheckService);
  private readonly badgeService = inject(ChangesBadgeService);
  readonly ticketPortal = inject(TicketPortalStore);

  constructor() {
    this.badgeService.refreshCounts();
    this.navbarService.initMobileDetection();
  }

  currentVersion = versionEmployee;
  private currentBuildDate = buildDateEmployee;
  environmentTier = environment.tier;

  private readonly router = inject(Router);
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  // Nicht gecacht: die Rollen-Ansicht (#1264) ändert die Claims zur Laufzeit.
  visibleGroups = computed(() =>
    visibleNavGroups({
      hasClaim: (claim) => this.userService.hasClaim(claim),
      isTeamlead: this.userService.isTeamlead(),
    })
  );

  isActive(entry: NavEntry): boolean {
    return matchesPath(this.url(), entry.activePaths ?? [entry.path]);
  }

  profileActive = computed(() => matchesPath(this.url(), ['settings/profile']));

  /** Zugänglicher Name: bei einer Zahl mit Zusatz, damit Screenreader sie nicht ohne Kontext vorlesen. */
  entryLabel(entry: NavEntry): string {
    const count = entry.badge ? this.badgeValue(entry.badge.key) : 0;
    return count > 0 ? `${entry.label}, ${count}` : entry.label;
  }

  badgeValue(key: NavBadgeKey): number {
    switch (key) {
      case 'alterations':
        return this.badgeService.alterationsCount();
      case 'duplicates':
        return this.badgeService.duplicatesCount();
      case 'export':
        return this.badgeService.exportCount();
      case 'tickets':
        return this.badgeService.unreadTicketsCount();
    }
  }

  private readonly employee = signal<Employee | null>(null);

  profileTile = computed(() =>
    buildProfileTile(
      this.employee() ?? { email: this.userService.currentUser()?.email ?? undefined },
      (claim) => this.userService.hasClaim(claim)
    )
  );

  private readonly loadEmployee = effect(() => {
    const uid = this.userService.currentUser()?.uid;
    if (!uid) return;
    // Name für die Profil-Kachel; ohne Treffer bleibt die E-Mail-Adresse als Fallback.
    this.userService
      .getUser(uid)
      .then((employee) => this.employee.set(employee))
      .catch(() => this.employee.set(null));
  });

  private readonly routerService = inject(RouterService);
  readonly roleLabels = SIMULATED_ROLE_LABELS;

  simulatedRoleLabel = computed(() => {
    const role = this.userService.simulatedRole();
    return role ? this.roleLabels[role] : '';
  });

  stopRoleSimulation(): void {
    this.userService.stopRoleSimulation();
    this.routerService.reloadOnDefaultRoute();
  }

  /** „Frage stellen“ (#1175): öffnet das Feedback-Widget aus dem Fuß der Seitenleiste. */
  openSupport(): void {
    this.navbarService.closeSidenavOnMobile();
    this.ticketPortal.requestOpen();
  }

  openVersionDialog() {
    this.updateCheckService.openVersionDialog(
      this.currentVersion,
      this.currentBuildDate
    );
  }
}
